import { Grid, Button, ButtonGroup, Chip } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";

import { scbsmbLossMonthlyAction } from "../../actions/energyAction";
import GraphLoader from "../../components/GraphLoader";
import Printer2 from "../../components/Printer2";
import { forecastingScbsmbStringDeviationAction } from "../../actions/predictiveAction";

/* ##### Heatmap Component  ##### */

const HeatmapPred = ({ databaseStr, scbsmbDetail, selectedLebel }) => {
  const { forecastedScbsmbstringDeviation: scbsmbLoss, scbsmbstringDeviationLoading: loading } = useSelector((state) => state.forecasting);
  const { user } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const { projectDetail } = useSelector((state) => state.projectDetail);
  const [uniqueKyes, setUniqueKyes] = useState([]);
  const [title, setTitle] = useState(null);
  
  // State for time period selection
  const [selectedPeriod, setSelectedPeriod] = useState("historical");
  const [filterBelowZero, setFilterBelowZero] = useState(false);

  // Get the current date range type from selectedLebel
  const currentDateRangeType = selectedLebel?.dateRangeType || "month";

  // Dynamic time period options based on dateRangeType
  const getTimePeriods = () => {
    switch (currentDateRangeType) {
      case "5_min":
        return [
          { label: "Historical", value: "historical", description: "View past data (5-min)" },
          { label: "Next 1 Hour", value: "1hour", description: "Forecast next 1 hour" },
          { label: "Next 2 Hours", value: "2hours", description: "Forecast next 2 hours" },
        ];
      case "day":
        return [
          { label: "Historical", value: "historical", description: "View past data (daily)" },
          { label: "Next 7 Days", value: "7days", description: "Forecast next 7 days" },
          { label: "Next 30 Days", value: "30days", description: "Forecast next 30 days" },
        ];
      case "month":
        return [
          { label: "Historical", value: "historical", description: "View past data (monthly)" },
          { label: "Next 6 Months", value: "6months", description: "Forecast next 6 months" },
          { label: "Next Year", value: "year", description: "Forecast next year" },
        ];
      case "year":
        return [
          { label: "Historical", value: "historical", description: "View past data (yearly)" },
          { label: "Next 2 Years", value: "2years", description: "Forecast next 2 years" },
          { label: "Next 5 Years", value: "5years", description: "Forecast next 5 years" },
        ];
      default:
        return [
          { label: "Historical", value: "historical", description: "View past data" },
          { label: "Next 6 Months", value: "6months", description: "Forecast next 6 months" },
          { label: "Next Year", value: "year", description: "Forecast next year" },
        ];
    }
  };

  const timePeriods = getTimePeriods();

  // Helper function to get slice limit based on period and date range type
  const getSliceLimit = (period) => {
    switch (period) {
      case "1hour": return 12;
      case "2hours": return 24;
      case "7days": return 7;
      case "30days": return 30;
      case "6months": return 6;
      case "year": return 12;
      case "2years": return 2;
      case "5years": return 5;
      default: return null;
    }
  };

  // Helper function to get forecast value based on selected period
  const getForecastValue = (period) => {
    switch (period) {
      case "1hour": return "1hour";
      case "2hours": return "2hours";
      case "historical": return "historical";
      case "7days": return "7days";
      case "30days": return "30days";
      case "6months": return "6months";
      case "year": return "year";
      case "2years": return "2years";
      case "5years": return "5years";
      default: return "historical";
    }
  };

  // Helper function to safely format numbers
  const formatNumber = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return null;
    }
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (typeof num !== 'number' || isNaN(num)) {
      return null;
    }
    return num.toFixed(2);
  };

  // Memoize heatmap data processing with period filtering and null handling
  const heatmapData = useMemo(() => {
    if (!scbsmbLoss) return null;

    // Filter data based on selected time period
    let filteredData = [...scbsmbLoss];
    
    if (selectedPeriod !== "historical") {
      const sliceLimit = getSliceLimit(selectedPeriod);
     
      if (sliceLimit) {
        filteredData = filteredData.slice(0, sliceLimit);
      }
    }

    if (filteredData.length === 0) return null;

    const set = new Set();
    const processedData = filteredData?.map((e) => {
      const finalObj = { name: e["Date"] };
      const newObj = {};
      const countObj = {};

      Object.keys(e || {}).forEach((ee) => {
        if (ee !== "Date" && ee !== "predicted") {
          const stringKey =
            projectDetail?.plantDetail?.string === "STRING"
              ? ee?.substring(0, 9)
              : ee.substring(0, 6);

          set.add(stringKey);
          const value = e[ee];
          
          // Check if value is null, undefined, or NaN
          if (value === null || value === undefined || value === "null" || isNaN(Number(value))) {
            // Mark as null value - will show as empty/blank
            newObj[stringKey] = null;
            countObj[stringKey] = (countObj[stringKey] || 0) + 1;
          } else {
            const numValue = Number(value);
            if (!isNaN(numValue)) {
              newObj[stringKey] = (newObj[stringKey] || 0) + numValue;
              countObj[stringKey] = (countObj[stringKey] || 0) + 1;
            } else {
              newObj[stringKey] = null;
              countObj[stringKey] = (countObj[stringKey] || 0) + 1;
            }
          }
        }
      });

      Object.keys(newObj).forEach((key) => {
        if (newObj[key] === null) {
          // Keep null values as null - will show as blank/empty
          finalObj[key] = null;
        } else {
          const avgValue = newObj[key] / countObj[key];
          // Apply filter below zero only if value is not null
          if (filterBelowZero && avgValue >= 0) {
            finalObj[key] = null;
          } else {
            finalObj[key] = avgValue.toFixed(2);
          }
        }
      });

      return finalObj;
    }).filter(item => {
      if (filterBelowZero) {
        // Only keep items that have at least one negative non-null value
        return Object.values(item).some(value => 
          value !== null && !isNaN(value) && parseFloat(value) < 0
        );
      }
      return true;
    });

    setUniqueKyes([...set].sort());


    const result = processedData.map((e) => ({
      name: e.name,
      data: Object.entries(e)
        .filter(([key]) => key !== "name")
        .map(([x, y]) => ({
          x,
          // For null values, we use a special flag that will be handled by color scale
          y: y === null ? -999 : (!isNaN(y) ? parseFloat(y) : -999),
        })).sort((a, b) => a.x.localeCompare(b.x)),
    })).filter(item => item.data.length > 0);

    return result;
  }, [scbsmbLoss, projectDetail, selectedPeriod, filterBelowZero]);

  
  // Calculate min and max for color scale ranges based on actual data (excluding nulls)
  const colorRanges = useMemo(() => {
    return [ { from: -100, to: -16, name: "Severe Loss", color: "#FF0000" },
      { from: -15, to: -6, name: "Moderate Loss", color: "#FFB200" },
      { from: -5, to: -0.1, name: "Slight Loss", color: "#128FD9" },
      { from: 0, to: 100, name: "No Loss", color: "#00A100" },
      {
      from: -1000,
      to: -998,
      name: "No Data",
      color: "#ffffff"
    }
    ];

    // if (!heatmapData) return [
    //   { from: -100, to: -16, name: "Severe Negative", color: "#FF0000" },
    //   { from: -15, to: -6, name: "Moderate Negative", color: "#FFB200" },
    //   { from: -5, to: -0.1, name: "Slight Negative", color: "#128FD9" },
    //   { from: 0, to: 100, name: "Positive/No Loss", color: "#00A100" },
    // ];

    // // Find min and max values from data (excluding nulls which are -999)
    // let minValue = Infinity;
    // let maxValue = -Infinity;
    
    // heatmapData.forEach(series => {
    //   series.data.forEach(point => {
    //     if (point.y !== null && point.y !== -999 && !isNaN(point.y)) {
    //       const val = parseFloat(point.y);
    //       if (val < minValue) minValue = val;
    //       if (val > maxValue) maxValue = val;
    //     }
    //   });
    // });

    // if (minValue === Infinity) minValue = -10;
    // if (maxValue === -Infinity) maxValue = 10;

    // // Create dynamic ranges based on actual data
    // const ranges = [];
    
    // // Positive ranges
    // if (maxValue > 0) {
    //   ranges.push({
    //     from: 0.01,
    //     to: maxValue,
    //     name: "Positive",
    //     color: "#00A100"
    //   });
    // }
    
    // // Negative ranges
    // if (minValue < 0) {
    //   if (minValue <= -15) {
    //     ranges.push({
    //       from: -15,
    //       to: -0.01,
    //       name: "Moderate Negative",
    //       color: "#FFB200"
    //     });
    //     if (minValue <= -25) {
    //       ranges.push({
    //         from: -25,
    //         to: -15.01,
    //         name: "Severe Negative",
    //         color: "#FF0000"
    //       });
    //     }
    //   } else if (minValue <= -5) {
    //     ranges.push({
    //       from: minValue,
    //       to: -0.01,
    //       name: "Negative",
    //       color: "#FFB200"
    //     });
    //   } else {
    //     ranges.push({
    //       from: minValue,
    //       to: -0.01,
    //       name: "Slight Negative",
    //       color: "#128FD9"
    //     });
    //   }
    // }
    
    // // Add empty/blank range for missing data (transparent)
    // ranges.push({
    //   from: -1000,
    //   to: -998,
    //   name: "No Data",
    //   color: "transparent"
    // });
    
    // return ranges;
  }, [heatmapData]);

  // Handle period change
  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
  };

  // Handle filter toggle
  const handleFilterToggle = () => {
    setFilterBelowZero(!filterBelowZero);
  };

  // Reset selected period when date range type changes
  useEffect(() => {
    setSelectedPeriod("historical");
  }, [currentDateRangeType]);

  useEffect(() => {
    let { inverter, block, scbsmb, stack } = scbsmbDetail;
    inverter = inverter?.split(" ")[1];
    block = block?.split(" ")[1];
    scbsmb = scbsmb?.split(" ")[1];
    stack = stack?.split(" ")[1];
    setTitle(
      (projectDetail?.plantDetail?.block_name || "BL") +
        "-" +
        block +
        "-" +
        "INV-" +
        inverter +
        (stack ? "-STACK-" + stack : "")
    );
  }, [scbsmbDetail]);

  useEffect(() => {
    if (!selectedPeriod || !selectedLebel?.dateRangeType || !scbsmbDetail) return;

    const timer = setTimeout(() => {
      const timeRange = getForecastValue(selectedPeriod);
      dispatch(
        forecastingScbsmbStringDeviationAction(
          scbsmbDetail,
          { ...selectedLebel, forecast: timeRange },
          databaseStr
        )
      );
    }, 300);

    return () => clearTimeout(timer);
  }, [
    scbsmbDetail,
    selectedPeriod,
    selectedLebel?.dateRangeType,
    databaseStr,
    dispatch
  ]);

  const todayDate = new Date().toLocaleDateString();
  const startDate = selectedLebel?.startDate
    ? new Date(selectedLebel?.startDate).toLocaleDateString()
    : null;
  const endDate = selectedLebel?.endDate
    ? new Date(selectedLebel?.endDate).toLocaleDateString()
    : null;

  const dynamicChartHeight = Math.max((heatmapData?.length || 0) * 45, 400);
  const chartRef = useRef(null);

  // Count null values for summary
  const nullValueCount = useMemo(() => {
    if (!heatmapData) return 0;
    let count = 0;
    heatmapData.forEach(series => {
      series.data.forEach(point => {
        if (point.y === -999) count++;
      });
    });
    return count;
  }, [heatmapData]);

  const getDisplayUnit = () => {
    switch (currentDateRangeType) {
      case "5_min": return "5-min";
      case "day": return "Daily";
      case "month": return "Monthly";
      case "year": return "Yearly";
      default: return "Monthly";
    }
  };

  const getUnitLabel = () => {
    switch (currentDateRangeType) {
      case "5_min": return "intervals";
      case "day": return "days";
      case "month": return "months";
      case "year": return "years";
      default: return "months";
    }
  };

  return (
    <>
      <Grid container textAlign={"center"}>
        <Grid
          item
          sx={{ boxShadow: 2 }}
          xl={11.8}
          lg={11.8}
          md={11.8}
          sm={11.8}
          xs={11.8}
          style={{
            background: "linear-gradient(to bottom, rgb(3, 99, 125),rgb(4, 128, 162),rgb(4, 135, 172))",
            margin: "1vmax auto",
            padding: "0.5vmax",
          }}
        >
          <Grid
            item
            lg={12}
            sx={{
              position: "relative",
              backgroundColor: "white",
              padding: "1rem 0",
              minHeight: "85vh",
              height: "fit-content",
            }}
          >
            {/* Header with Time Period Selection */}
            <div
              style={{
                padding: "1rem 1.5rem",
                position: "sticky",
                left: "0",
                right: "0",
                top: "0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                textAlign: "center",
                zIndex: "10",
                backgroundColor: "white",
                // borderBottom: "1px solid #f0f0f0",
                flexWrap: "wrap",
                gap: "1rem",
              }}
            >
              {/* Left side - Time Period Selection */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "0.5rem" }}>
                <label style={{ fontSize: "0.9rem", fontWeight: 500, color: "#333" }}>
                  Time Period ({getDisplayUnit()}):
                </label>
                <ButtonGroup variant="outlined" size="small" style={{ display: "flex", flexWrap: "wrap" }}>
                  {timePeriods.map((period) => (
                    <Button
                      key={period.value}
                      onClick={() => handlePeriodChange(period.value)}
                      variant={selectedPeriod === period.value ? "contained" : "outlined"}
                      style={{
                        backgroundColor: selectedPeriod === period.value ? "#1890ff" : "transparent",
                        color: selectedPeriod === period.value ? "white" : "#666",
                        borderColor: "#d0d0d0",
                        fontSize: "0.8rem",
                        padding: "4px 12px",
                        minWidth: "90px",
                      }}
                      title={period.description}
                    >
                      {period.label}
                    </Button>
                  ))}
                </ButtonGroup>
              </div>

              {/* Center - Title */}
              <div style={{ flex: 1, textAlign: "center" }}>
                <h3 style={{ margin: "0 0 5px 0", fontSize: "1rem" }}>
                  {selectedPeriod === "historical" ? "Historical" : "Forecasted"} Heatmap of {scbsmbDetail.scbsmbType}(s) Normalized-STC Current Loss (%) - {getDisplayUnit()} Data ({title})
                  {filterBelowZero && (
                    <Chip
                      label="Filtered"
                      size="small"
                      style={{
                        marginLeft: "8px",
                        backgroundColor: "#fff2e8",
                        color: "#fa541c",
                        borderColor: "#ffbb96",
                        fontSize: "0.7rem",
                        height: "20px",
                      }}
                      variant="outlined"
                    />
                  )}
                </h3>
              </div>

              {/* Right side - Filter and Printer */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.5rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <label
                    htmlFor="heatmap-filter"
                    style={{
                      fontSize: "0.85rem",
                      fontWeight: 500,
                      color: "#333",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <input
                      id="heatmap-filter"
                      type="checkbox"
                      checked={filterBelowZero}
                      onChange={handleFilterToggle}
                      style={{
                        width: "16px",
                        height: "16px",
                        cursor: "pointer",
                        accentColor: "#1890ff",
                      }}
                    />
                    Show Only Negative Loss
                  </label>
                </div>

                <div style={{ display: "flex", alignItems: "center" }}>
                  <Printer2
                    dataSetDetail={{
                      data: heatmapData,
                      name: `Heatmap Data - ${getDisplayUnit()} (${selectedPeriod !== "historical" ? "Forecasted" : "Historical"})`,
                      ref: chartRef,
                      refCurrent: chartRef.current,
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Legend for Heatmap Colors */}
            <div
              style={{
                position: "sticky",
                left: "0",
                top: "85px",
                zIndex: "10",
                backgroundColor: "rgba(255, 255, 255, 0.98)",
                padding: "10px 16px",
                // borderBottom: "1px solid #f0f0f0",
                // boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "20px",
                marginBottom: "10px",
              }}
            >
              {colorRanges.map((range, idx) => (
                <div key={idx} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      backgroundColor: range.color,
                      borderRadius: "3px",
                      border: range.name === "No Data" ? "1px dashed #ccc" : "none",
                    }}
                  />
                  <span style={{ fontSize: "0.75rem", color: "#666" }}>
                    {range.name === "No Data" ? "No Data (Empty)" : range.name}
                  </span>
                </div>
              ))}
            </div>

            {/* Graph Container */}
            {loading ? (
              <div
                style={{
                  height: "75vh",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <GraphLoader content={`${selectedPeriod !== "historical" ? "Forecasting" : "Generating"} Heatmap`} />
              </div>
            ) : heatmapData?.length > 0 ? (
              <div ref={chartRef}>
                <Chart
                  style={{
                    margin: "auto",
                    width: "95%",
                    minWidth: "500px",
                  }}
                  height={dynamicChartHeight}
                  type="heatmap"
                  options={{
                    chart: {
                      toolbar: {
                        show: user?.userLevel !== 1 ? false : true,
                      },
                      background: 'transparent',
                      animations: {
                        enabled: true,
                      },
                    },
                    plotOptions: {
                      heatmap: {
                        enableShades: false,
                        shadeIntensity: 0,
                        colorScale: {
                          ranges: colorRanges,
                        },
                        radius: 2,
                      },
                    },
                    xaxis: {
                      categories: uniqueKyes,
                      position: "top",
                      labels: {
                        style: {
                          fontSize: "0.75rem",
                          fontWeight: "600",
                          fontFamily: "Cambria, Cochin, Georgia, Times, 'Times New Roman', serif",
                        },
                        rotate: -45,
                        rotateAlways: uniqueKyes.length > 10,
                      },
                      // title: {
                      //   text: "String Identifier",
                      //   style: {
                      //     fontSize: "0.8rem",
                      //     fontWeight: "bold",
                      //   },
                      // },
                    },
                    yaxis: {
                      labels: {
                        style: {
                          fontSize: "0.75rem",
                          fontWeight: "600",
                          fontFamily: "Cambria, Cochin, Georgia, Times, 'Times New Roman', serif",
                        },
                      },
                      // title: {
                      //   text: "Date",
                      //   style: {
                      //     fontSize: "0.8rem",
                      //     fontWeight: "bold",
                      //   },
                      // },
                    },
                    grid: {
                      show: true,
                      borderColor: "#e0e0e0",
                      strokeDashArray: 0,
                    },
                    dataLabels: {
                      enabled: false,
                    },
                    legend: {
                      showForSingleSeries: true,
                      show: false,
                      position: "bottom",
                      horizontalAlign: "center",
                      fontSize: "11px",
                      fontFamily: "Cambria, Cochin, Georgia, Times, 'Times New Roman', serif",
                      markers: {
                        width: 12,
                        height: 12,
                      },
                    },
                    tooltip: {
                      y: {
                        formatter: (value) => {
                          if (value === -999) {
                            return "No Data Available";
                          }
                          return `${formatNumber(value)}%`;
                        },
                      },
                      style: {
                        fontSize: "12px",
                      },
                    },
                    states: {
                      hover: {
                        filter: {
                          type: 'darken',
                          value: 0.15,
                        },
                      },
                    },
                  }}
                  series={heatmapData || []}
                />
              </div>
            ) : (
              <div
                style={{
                  height: "75vh",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  gap: "1rem",
                }}
              >
                <h3>
                  {heatmapData?.length === 0 && todayDate === startDate && todayDate === endDate
                    ? "Data Not Available On SCADA API"
                    : filterBelowZero
                      ? "No negative loss data matches the filter criteria"
                      : "No data available for the selected period"}
                </h3>
                {filterBelowZero && heatmapData?.length === 0 && (
                  <button
                    onClick={() => setFilterBelowZero(false)}
                    style={{
                      padding: "0.5rem 1rem",
                      backgroundColor: "#1890ff",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "0.9rem",
                    }}
                  >
                    Show All Data
                  </button>
                )}
              </div>
            )}

            {/* Summary Section */}
            {/* {heatmapData?.length > 0 && (
              <div
                style={{
                  padding: "1rem",
                  borderTop: "1px solid #f0f0f0",
                  display: "flex",
                  justifyContent: "space-around",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "1rem",
                  marginTop: "1rem",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "0.8rem", color: "#666", marginBottom: "4px" }}>
                    Total {getUnitLabel() === "days" ? "Days" : getUnitLabel() === "years" ? "Years" : "Months"}
                  </div>
                  <div style={{ fontSize: "1.2rem", fontWeight: "bold", color: "#1890ff" }}>
                    {heatmapData.length}
                  </div>
                </div>
                
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "0.8rem", color: "#666", marginBottom: "4px" }}>
                    Strings Analyzed
                  </div>
                  <div style={{ fontSize: "1.2rem", fontWeight: "bold", color: "#52c41a" }}>
                    {uniqueKyes.length}
                  </div>
                </div>
                
                {nullValueCount > 0 && (
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "0.8rem", color: "#666", marginBottom: "4px" }}>
                      Missing Data Points
                    </div>
                    <div style={{ fontSize: "1.2rem", fontWeight: "bold", color: "#fa541c" }}>
                      {nullValueCount}
                    </div>
                  </div>
                )}
                
                {filterBelowZero && (
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "0.8rem", color: "#666", marginBottom: "4px" }}>
                      Periods with Loss
                    </div>
                    <div style={{ fontSize: "1.2rem", fontWeight: "bold", color: "#ff4d4f" }}>
                      {heatmapData.length}
                    </div>
                  </div>
                )}
                
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "0.8rem", color: "#666", marginBottom: "4px" }}>
                    Selected Period
                  </div>
                  <div style={{ fontSize: "1rem", fontWeight: "500", color: "#333" }}>
                    {timePeriods.find(p => p.value === selectedPeriod)?.label}
                  </div>
                </div>

                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "0.8rem", color: "#666", marginBottom: "4px" }}>
                    Data Type
                  </div>
                  <div style={{ fontSize: "1rem", fontWeight: "500", color: "#333" }}>
                    {getDisplayUnit()}
                  </div>
                </div>
              </div>
            )} */}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default HeatmapPred;